// Environment configuration and validation
export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentConfig {
  environment: Environment;
  supabase: {
    url: string;
    anonKey: string;
    projectId: string;
  };
  security: {
    enableCSP: boolean;
    enableRateLimiting: boolean;
    enableAuditLogging: boolean;
    enableErrorTracking: boolean;
  };
  features: {
    enableAnalytics: boolean;
    enablePerformanceMonitoring: boolean;
    enableDebugMode: boolean;
    enableMaintenance: boolean;
  };
  api: {
    timeout: number;
    retryAttempts: number;
    enableCaching: boolean;
  };
}

// Environment detection
export const getEnvironment = (): Environment => {
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname.includes('staging') || hostname.includes('preview')) {
    return 'staging';
  }
  
  return 'production';
};

// Configuration for different environments
const configs: Record<Environment, EnvironmentConfig> = {
  development: {
    environment: 'development',
    supabase: {
      url: 'https://kllsxgvvwosxlcpxqwin.supabase.co',
      anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtsbHN4Z3Z2d29zeGxjcHhxd2luIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzOTU4MjUsImV4cCI6MjA3MTk3MTgyNX0.O5tvKJWH3ord5DEIblozDUUee9jIPOAhBRKk1IwVANM',
      projectId: 'kllsxgvvwosxlcpxqwin',
    },
    security: {
      enableCSP: false, // Disabled in dev for easier debugging
      enableRateLimiting: false,
      enableAuditLogging: true,
      enableErrorTracking: true,
    },
    features: {
      enableAnalytics: false,
      enablePerformanceMonitoring: true,
      enableDebugMode: true,
      enableMaintenance: false,
    },
    api: {
      timeout: 10000,
      retryAttempts: 1,
      enableCaching: false,
    },
  },
  staging: {
    environment: 'staging',
    supabase: {
      url: 'https://kllsxgvvwosxlcpxqwin.supabase.co',
      anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtsbHN4Z3Z2d29zeGxjcHhxd2luIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzOTU4MjUsImV4cCI6MjA3MTk3MTgyNX0.O5tvKJWH3ord5DEIblozDUUee9jIPOAhBRKk1IwVANM',
      projectId: 'kllsxgvvwosxlcpxqwin',
    },
    security: {
      enableCSP: true,
      enableRateLimiting: true,
      enableAuditLogging: true,
      enableErrorTracking: true,
    },
    features: {
      enableAnalytics: true,
      enablePerformanceMonitoring: true,
      enableDebugMode: true,
      enableMaintenance: false,
    },
    api: {
      timeout: 8000,
      retryAttempts: 2,
      enableCaching: true,
    },
  },
  production: {
    environment: 'production',
    supabase: {
      url: 'https://kllsxgvvwosxlcpxqwin.supabase.co',
      anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtsbHN4Z3Z2d29zeGxjcHhxd2luIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzOTU4MjUsImV4cCI6MjA3MTk3MTgyNX0.O5tvKJWH3ord5DEIblozDUUee9jIPOAhBRKk1IwVANM',
      projectId: 'kllsxgvvwosxlcpxqwin',
    },
    security: {
      enableCSP: true,
      enableRateLimiting: true,
      enableAuditLogging: true,
      enableErrorTracking: true,
    },
    features: {
      enableAnalytics: true,
      enablePerformanceMonitoring: true,
      enableDebugMode: false,
      enableMaintenance: false,
    },
    api: {
      timeout: 5000,
      retryAttempts: 3,
      enableCaching: true,
    },
  },
};

// Get current environment configuration
export const getConfig = (): EnvironmentConfig => {
  const env = getEnvironment();
  return configs[env];
};

// Configuration validation
export const validateConfig = (config: EnvironmentConfig): boolean => {
  const requiredFields = [
    'supabase.url',
    'supabase.anonKey',
    'supabase.projectId',
  ];

  for (const field of requiredFields) {
    const value = field.split('.').reduce((obj, key) => obj?.[key], config as any);
    if (!value) {
      console.error(`Missing required configuration: ${field}`);
      return false;
    }
  }

  // Validate URL format
  try {
    new URL(config.supabase.url);
  } catch {
    console.error('Invalid Supabase URL');
    return false;
  }

  // Validate project ID format
  if (!/^[a-z0-9]{20}$/.test(config.supabase.projectId)) {
    console.error('Invalid Supabase project ID format');
    return false;
  }

  return true;
};

// Health check for environment
export const performHealthCheck = async (): Promise<{
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks: Record<string, boolean>;
  timestamp: string;
}> => {
  const config = getConfig();
  const checks: Record<string, boolean> = {};

  // Check configuration validity
  checks.config = validateConfig(config);

  // Check local storage availability
  try {
    localStorage.setItem('health_check', 'test');
    localStorage.removeItem('health_check');
    checks.localStorage = true;
  } catch {
    checks.localStorage = false;
  }

  // Check network connectivity
  checks.network = navigator.onLine;

  // Check Supabase connectivity
  try {
    const response = await fetch(`${config.supabase.url}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'apikey': config.supabase.anonKey,
      },
      signal: AbortSignal.timeout(5000),
    });
    checks.supabase = response.ok;
  } catch {
    checks.supabase = false;
  }

  // Determine overall status
  const failedChecks = Object.values(checks).filter(check => !check).length;
  let status: 'healthy' | 'degraded' | 'unhealthy';

  if (failedChecks === 0) {
    status = 'healthy';
  } else if (failedChecks <= 1) {
    status = 'degraded';
  } else {
    status = 'unhealthy';
  }

  return {
    status,
    checks,
    timestamp: new Date().toISOString(),
  };
};

// Feature flags
export const isFeatureEnabled = (feature: keyof EnvironmentConfig['features']): boolean => {
  const config = getConfig();
  return config.features[feature];
};

export const isSecurityEnabled = (security: keyof EnvironmentConfig['security']): boolean => {
  const config = getConfig();
  return config.security[security];
};

// Debug utilities
export const getDebugInfo = () => {
  if (!isFeatureEnabled('enableDebugMode')) {
    return null;
  }

  const config = getConfig();
  return {
    environment: config.environment,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: screen.width,
      height: screen.height,
    },
    memory: (performance as any).memory ? {
      used: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024),
    } : null,
    localStorage: {
      available: typeof localStorage !== 'undefined',
      usage: (() => {
        try {
          let total = 0;
          for (const key in localStorage) {
            if (localStorage.hasOwnProperty(key)) {
              total += localStorage[key].length + key.length;
            }
          }
          return Math.round(total / 1024); // KB
        } catch {
          return 0;
        }
      })(),
    },
  };
};

// Maintenance mode
export const isMaintenanceMode = (): boolean => {
  const config = getConfig();
  return config.features.enableMaintenance;
};

export const getMaintenanceInfo = () => {
  return {
    enabled: isMaintenanceMode(),
    message: 'The application is currently undergoing maintenance. Please check back later.',
    estimatedDuration: 'Unknown',
    contact: 'support@solrdrinks.com',
  };
};

export default getConfig;